import { BiUserPlus } from 'react-icons/bi';
import { FiSun } from 'react-icons/fi';
import { GiCruiser } from 'react-icons/gi';
import { IoMdCheckboxOutline } from 'react-icons/io';
import { IoImageOutline } from 'react-icons/io5';
import { LuMessageSquare } from 'react-icons/lu';
import { PiCurrencyDollarLight } from 'react-icons/pi';
import { TbMapPin2 } from 'react-icons/tb';

import type { Permission } from '@/permissions/permissions.catalog';

import { getPrimaryRole, UserRole } from './roles';
import type { CurrencyUnit, LengthUnit, VolumeUnit } from './unitConversion';

export type UnitPreferences = {
  length: LengthUnit;
  volume: VolumeUnit;
  // Optional: blobs saved before the currency switch existed do not carry it.
  currency?: CurrencyUnit;
};

export const BUILTIN_DASHBOARD_TYPES = [
  'owner',
  'captain',
  'crew',
  'service_provider',
  'guest',
  'no_role',
] as const;
export type BuiltInDashboardType = (typeof BUILTIN_DASHBOARD_TYPES)[number];
export type DashboardType = BuiltInDashboardType | string;

export const ROLE_TO_DASHBOARD_TYPE: Record<UserRole, BuiltInDashboardType> = {
  [UserRole.OWNER]: 'owner',
  [UserRole.CO_OWNER]: 'owner',
  [UserRole.CAPTAIN]: 'captain',
  [UserRole.ENGINEER]: 'crew',
  [UserRole.CREW_MEMBER]: 'crew',
  [UserRole.CHEF]: 'crew',
  [UserRole.MATE_DECKHAND]: 'crew',
  [UserRole.STEWARD]: 'crew',
  [UserRole.SERVICE_PROVIDER]: 'service_provider',
  [UserRole.GUEST]: 'guest',
};

export type ColSpan = 1 | 2 | 3 | 4;

export type WidgetId =
  | 'at_a_glance'
  | 'chosen_yacht'
  | 'needs_attention'
  | 'next_event'
  | 'weather'
  | 'whos_on_board'
  | 'budget'
  | 'our_memories'
  | 'chat'
  | 'projects'
  | 'logs'
  | 'crew_roster'
  | 'invoices_to_pay'
  | 'financial_status'
  | 'spend_by_category'
  | 'maintenance_tasks';

export type WidgetLayoutItem = {
  id: WidgetId;
  visible: boolean;
  order: number;
  col_span: ColSpan;
};

export type PlatformLayout = {
  widgets: WidgetLayoutItem[];
};

export type DashboardLayout = {
  desktop: PlatformLayout;
  mobile: PlatformLayout;
};

export type UserDashboardSettings = {
  dashboards: Record<DashboardType, DashboardLayout>;
  unitPreferences?: UnitPreferences;
  // Backend stores this blob as opaque JSON; unknown keys are preserved on write.
  [key: string]: unknown;
};

export type WidgetCategory =
  | 'vessel'
  | 'events'
  | 'finances'
  | 'crew'
  | 'tasks'
  | 'weather'
  | 'media'
  | 'chat';

export const CATEGORY_ICONS: Record<WidgetCategory, React.ReactElement> = {
  vessel: <GiCruiser />,
  events: <TbMapPin2 />,
  finances: <PiCurrencyDollarLight />,
  crew: <BiUserPlus />,
  tasks: <IoMdCheckboxOutline />,
  weather: <FiSun />,
  media: <IoImageOutline />,
  chat: <LuMessageSquare />,
};

export const CATEGORY_GROUP_LABEL: Record<WidgetCategory, string> = {
  vessel: 'Overview',
  tasks: 'Overview',
  events: 'Plan',
  weather: 'Plan',
  crew: 'Crew',
  finances: 'Finances',
  media: 'Media',
  chat: 'Messages',
};

export type WidgetDefinition = {
  id: WidgetId;
  label: string;
  category: WidgetCategory;
  component: React.ComponentType<any>;
  minColSpan: ColSpan;
  maxColSpan: ColSpan;
  defaultColSpan: ColSpan;
  mobileSupported: boolean;
  lockedOnMobile?: boolean;
  lockedOnDesktop?: boolean;
  allowedPermissions: Permission[];
  disallowedRoles?: readonly UserRole[];
};

export function getActiveDashboardType(roles: UserRole[], customRoleId?: string): DashboardType {
  if (customRoleId) return customRoleId;
  const primary = getPrimaryRole(roles);
  return primary ? ROLE_TO_DASHBOARD_TYPE[primary] : 'no_role';
}
